import { CustomToastComponent } from "@/components/shared";
import { Document, DocumentStatus } from "@/graphql/__generated__/graphql";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AddDocumentToTenderMutation,
  AddDocumentToTenderMutationVariables,
  AddDocumentToTenderDocument,
  FetchDocumentsByTenderIdDocument,
} from "@/graphql/__generated__/graphql";

/**
 * Custom hook for adding document to tender and handling the necessary cache updates.
 * @returns {Object} - Contains the AddDocumentToTender function, loading state, and error state.
 */
export default function useAddDocumentToTenderMutationHook(setMutationPollingActive: Dispatch<SetStateAction<boolean>>): { AddDocumentToTender: any, loading: any, error: any} {

  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [refetchDocumentsByTenderId] = useLazyQuery(FetchDocumentsByTenderIdDocument, {
    fetchPolicy: "network-only"
  });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const [AddDocumentToTender, { loading, error }] = useMutation<AddDocumentToTenderMutation, AddDocumentToTenderMutationVariables>(
    AddDocumentToTenderDocument, {
      onCompleted: async (data, clientOptions) => {
        const documentId = data.addDocumentToTender?.id;
        if (!documentId) return;

        const shouldPoll = clientOptions?.context?.addToKnowledgeBase;

        toast(CustomToastComponent, {
          toastId: "document-added-success",
          data: {
            title: t("toasts.documentaddedsuccessfully"),
            type: "success"
          }
        });

        // If document was added to knowledge base, poll for updates
        if (shouldPoll) {
          const mutationPollingCompleted = () => {
            setMutationPollingActive(false);
          };

          setMutationPollingActive(true);

          const pollInterval = 10000; // 10 seconds
          const maxTimeout = 10 * 60 * 1000; // 10 minutes maximum
          const startTime = Date.now();

          const pollForUpdates = async () => {
            if (Date.now() - startTime > maxTimeout) {
              console.error("Timeout reached while adding document to tender");
              mutationPollingCompleted();
              return;
            }

            const result = await refetchDocumentsByTenderId({
              variables: { tenderId: Number(tenderID) },
            });

            const documents = result.data?.fetchDocumentsByTenderId || [];
            const targetDocument = documents.find((doc: Document) => doc.id === documentId) as Document;

            if (targetDocument) {
              if (targetDocument.status === DocumentStatus.InKnowledgeBase) {
                toast(CustomToastComponent, {
                  toastId: "document-added-to-kb-success",
                  data: {
                    title: t("toasts.documentaddedtoknowledgebasesuccessfully"),
                    type: "success"
                  }
                });
                mutationPollingCompleted();
                return;
              } else if (targetDocument.status === DocumentStatus.NotInKnowledgeBase) {
                toast(CustomToastComponent, {
                  toastId: "document-added-to-kb-failed",
                  data: {
                    title: t("toasts.documentaddedtoknowledgebasefailed"),
                    type: "error"
                  }
                });
                mutationPollingCompleted();
                return;
              } else if (targetDocument.status === DocumentStatus.AddingToKnowledgeBase) {
              // Clear any existing timeout before setting a new one
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(pollForUpdates, pollInterval);
              }
            } else {
              mutationPollingCompleted();
              return;
            }
          };

          pollForUpdates();
        } else {
        // If not added to knowledge base, just refetch once to update the list
          refetchDocumentsByTenderId({
            variables: { tenderId: Number(tenderID) },
          });
        }
      }
    });

  return {
    AddDocumentToTender,
    loading,
    error
  };
}
